import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { authService } from '@/services/auth';
import { API_BASE_URL } from '@/constants/api';
import axios from 'axios';
import logo from '@/logo.svg';

// Screenshot imports using require
const keywordResearchScreenshot = require('../../assets/images/screenshots/kwyword_research.jpeg');
const analyticsScreenshot = require('../../assets/images/screenshots/analytics.jpeg');

interface FormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [typingText, setTypingText] = useState<string>('');
  const [currentTextIndex, setCurrentTextIndex] = useState<number>(0);
  const [isTyping, setIsTyping] = useState<boolean>(true);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const typingOptions = useMemo(() => ['keyword research', 'keyword clustering', 'page analytics'], []);

  const screenshots = [
    {
      image: keywordResearchScreenshot,
      title: "Advanced Keyword Research",
      description: "Discover high-value keywords with comprehensive analysis and competitive insights"
    },
    {
      image: analyticsScreenshot,
      title: "Measurable Analytics",
      description: "Track performance with detailed insights and data-driven optimization"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % screenshots.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [screenshots.length]);

  useEffect(() => {
    const typeSpeed = 100; // Speed for typing each character
    const deleteSpeed = 50; // Speed for deleting each character
    const pauseTime = 1000; // Time to pause before starting next word

    const typeText = () => {
      const currentText = typingOptions[currentTextIndex];
      
      if (isTyping && typingText.length < currentText.length) {
        // Typing phase
        setTimeout(() => {
          setTypingText(currentText.slice(0, typingText.length + 1));
        }, typeSpeed);
      } else if (isTyping && typingText.length === currentText.length) {
        // Finished typing, pause then start deleting
        setTimeout(() => {
          setIsTyping(false);
          setIsDeleting(true);
        }, pauseTime);
      } else if (isDeleting && typingText.length > 0) {
        // Deleting phase
        setTimeout(() => {
          setTypingText(typingText.slice(0, -1));
        }, deleteSpeed);
      } else if (isDeleting && typingText.length === 0) {
        // Finished deleting, move to next word
        setIsDeleting(false);
        setCurrentTextIndex((prev) => (prev + 1) % typingOptions.length);
        setIsTyping(true);
      }
    };

    const interval = setInterval(typeText, 50);
    return () => clearInterval(interval);
  }, [typingText, currentTextIndex, isTyping, isDeleting, typingOptions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Login using axios directly
      const response = await axios.post(`${API_BASE_URL}/auth/login/`, {
        email: formData.email,
        password: formData.password
      });

      const { token, user_data, organization } = response.data;

      // Transform API response to match our internal structure
      const authToken = token.access;
      const refreshToken = token.refresh;
      
      const userData = {
        id: user_data.pk.toString(),
        email: user_data.email,
        name: user_data.name.split(' ')[0] || '',
        lastName: user_data.name.split(' ').slice(1).join(' ') || '',
        role: user_data.role,
        permissions: user_data.permissions,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const orgData = {
        id: organization.id.toString(),
        name: organization.name,
        plan: 'basic',
        settings: {},
        createdAt: organization.created_at,
        updatedAt: organization.updated_at,
      };

      // Store tokens and data using authService
      authService.setTokens(refreshToken, authToken);
      authService.setUserData(userData);
      authService.setOrgData(orgData);

      // Redirect to research page after successful login
      navigate('/research');
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <style>
        {`
          :root {
            --logo-gradient-from: #818cf8;
            --logo-gradient-to: #a855f7;
          }
          
          @keyframes slideInUp {
            from {
              transform: translateY(1rem);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          .animate-slideInUp {
            animation: slideInUp 0.6s ease-out forwards;
          }
          

        `}
      </style>
      {/* Left Side - Screenshot Carousel */}
      <div className="hidden lg:flex lg:w-2/3 bg-gradient-to-br via-primary/5 to-background relative overflow-hidden">
        {/* Logo Block - Top Left */}
        {/* <div className="absolute top-8 left-8 z-20">
          <div className="flex items-center space-x-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[var(--logo-gradient-from)] to-[var(--logo-gradient-to)] flex items-center justify-center">
              <img src={logo} alt="Serpware Logo" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-sm font-semibold">Serpware</h1>
              <p className="text-xs text-muted-foreground">Scout</p>
            </div>
          </div>
        </div> */}
        <div className="flex flex-col justify-center items-center w-full px-16 relative z-10">
          <div className="max-w-3xl space-y-16 text-center">
            {/* Hero Section */}
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="text-4xl font-bold leading-tight text-left">
                  <span className="text-foreground">Supercharge your </span>
                  <span className="bg-gradient-to-r from-[var(--logo-gradient-from)] to-[var(--logo-gradient-to)] bg-clip-text text-transparent relative">
                    {typingText}
                    <span className="ml-1 animate-pulse">|</span>
                  </span>
                </h1>
                <p className="text-xl text-secondary-foreground leading-relaxed max-w-2xl mx-auto">
                  Result-oriented SEO insights that drive real business growth and measurable success
                </p>
              </div>
              
              {/* Key Benefits */}
              <div className="flex justify-center space-x-8 pt-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">500K+</div>
                  <div className="text-sm text-secondary-foreground">Keywords Analyzed</div>
                </div>
                <div className="w-px bg-border"></div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">98%</div>
                  <div className="text-sm text-secondary-foreground">Accuracy Rate</div>
                </div>
                <div className="w-px bg-border"></div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">24/7</div>
                  <div className="text-sm text-secondary-foreground">Real-time Data</div>
                </div>
              </div>
            </div>
            
            {/* Screenshot Carousel */}
            <div className="relative w-full max-w-2xl mx-auto">
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                {screenshots.map((screenshot, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-1000 ${
                      index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                    }`}
                  >
                    <img
                      src={screenshot.image}
                      alt={screenshot.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="bg-black/30 backdrop-blur-sm rounded-lg p-4">
                        <h3 className="text-white font-semibold text-lg mb-2 animate-slideInUp">{screenshot.title}</h3>
                        <p className="text-white/90 text-sm leading-relaxed animate-slideInUp" style={{ animationDelay: '0.2s' }}>{screenshot.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Carousel Indicators */}
              <div className="flex justify-center space-x-3 mt-6">
                {screenshots.map((_, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-primary scale-125' 
                        : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    }`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/3 bg-white/5 ring-1 ring-white/10 rounded-2xl backdrop-blur-sm">
        <div className="flex flex-col h-full p-8">
          {/* Header */}
          <div className="flex-shrink-0 mb-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[var(--logo-gradient-from)] to-[var(--logo-gradient-to)] flex items-center justify-center">
                  <img src={logo} alt="Serpware Logo" className="w-4 h-4" />
                </div>
                <div>
                  <h1 className="text-sm font-semibold text-muted-foreground">Serpware</h1>
                  <p className="text-xs text-secondary-foreground">Scout</p>
                </div>
              </div>
              <div className="space-y-1">
                <h2 className="text-2xl font-bold text-muted-foreground">Welcome back</h2>
                <p className="text-sm text-secondary-foreground">
                  Sign in to continue your SEO journey
                </p>
              </div>
            </div>
          </div>

          {/* Login Form */}
          <div className="flex-1">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="text-sm">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium text-muted-foreground">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-10 h-10 bg-background/30 border-border/30 focus:border-primary/50 focus:ring-primary/20 text-sm"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-medium text-muted-foreground">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-10 pr-10 h-10 bg-background/30 border-border/30 focus:border-primary/50 focus:ring-primary/20 text-sm"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-muted-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center space-x-2">
                  <input
                    id="remember"
                    type="checkbox"
                    className="rounded border-border/30 bg-background/30 focus:ring-primary/20"
                  />
                  <Label htmlFor="remember" className="text-xs text-secondary-foreground">
                    Remember me
                  </Label>
                </div>
                <Button
                  type="button"
                  variant="link"
                  className="text-xs px-0 text-primary hover:text-primary/80"
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot password?
                </Button>
              </div>

              <Button
                type="submit"
                className="w-full h-10 bg-gradient-to-r from-[var(--logo-gradient-from)] to-[var(--logo-gradient-to)] hover:from-[var(--logo-gradient-from)]/90 hover:to-[var(--logo-gradient-to)]/90 text-white font-medium rounded-lg transition-all duration-200 shadow-lg text-sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Sign Up Link */}
            <div className="mt-6 text-center">
              <p className="text-xs text-secondary-foreground">
                Don't have an account?{' '}
                <Button
                  type="button"
                  variant="link"
                  className="px-0 text-primary hover:text-primary/80 font-medium text-xs"
                  onClick={() => navigate('/register')}
                >
                  Sign up
                </Button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 